// ABOUTME: Table controls component providing search, filtering, and sorting functionality
// ABOUTME: for the virtual list of data items with category-based filtering and multiple sort options

import React, { useState, useCallback } from 'react';
import { DataItem } from '../types';

export interface TableControlsProps {
    onSearchChange: (searchTerm: string) => void;
    onSortChange: (sortBy: keyof DataItem, sortOrder: 'asc' | 'desc') => void;
    onFilterChange: (filterBy: string) => void;
    onExpandChange?: (isExpanded: boolean) => void;
    categories: string[];
    currentSort: { sortBy: keyof DataItem; sortOrder: 'asc' | 'desc' };
    currentFilter: string;
    currentSearch: string;
}

const TableControls: React.FC<TableControlsProps> = ({
    onSearchChange,
    onSortChange,
    onFilterChange,
    onExpandChange,
    categories,
    currentSort,
    currentFilter,
    currentSearch
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        onSearchChange(value);
    }, [onSearchChange]);

    const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const [sortBy, sortOrder] = e.target.value.split('-') as [keyof DataItem, 'asc' | 'desc'];
        onSortChange(sortBy, sortOrder);
    }, [onSortChange]);

    const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        onFilterChange(e.target.value);
    }, [onFilterChange]);

    const clearSearch = useCallback(() => {
        onSearchChange('');
    }, [onSearchChange]);

    return (
        <div className="bg-slate-700  mb-2">
            {/* Minimized Filter Toggle */}
            <div 
                className="flex items-center justify-between p-2 cursor-pointer hover:bg-slate-600/50 transition-colors "
                onClick={() => {
                    const newExpanded = !isExpanded;
                    setIsExpanded(newExpanded);
                    onExpandChange?.(newExpanded);
                }}
            >
                <div className="flex items-center space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            const newExpanded = !isExpanded;
                            setIsExpanded(newExpanded);
                            onExpandChange?.(newExpanded);
                        }}
                        className="p-1 text-slate-400 hover:text-white hover:bg-slate-600  transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
                        title={isExpanded ? "Hide filters" : "Show filters"}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                    </button>
                    <span className="text-sm text-slate-300 select-none">
                        {isExpanded ? 'Hide Filters' : 'Show Filters'}
                        {(currentSearch || currentFilter !== '') && (
                            <span className="ml-1 text-xs text-blue-400">(active)</span>
                        )}
                    </span>
                </div>
                
                {/* Quick actions when minimized */}
                {!isExpanded && currentSearch && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            clearSearch();
                        }}
                        className="px-2 py-1 text-xs text-slate-400 hover:text-white bg-slate-600 hover:bg-slate-500  transition-colors"
                        title="Clear search"
                    >
                        Clear: "{currentSearch.length > 20 ? currentSearch.substring(0, 20) + '...' : currentSearch}"
                    </button>
                )}
            </div>

            {/* Expanded Filter Section */}
            {isExpanded && (
                <div className="p-3 pt-0 space-y-3">
                    <div className="flex flex-col md:flex-row gap-3">
                        {/* Search */}
                        <div className="flex-1">
                            <label htmlFor="search" className="block text-xs font-medium text-slate-300 mb-1">
                                Search
                            </label>
                            <div className="relative">
                                <input
                                    id="search"
                                    type="text"
                                    value={currentSearch}
                                    onChange={handleSearchChange}
                                    placeholder="Search by name or description..."
                                    className="w-full px-3 py-1.5 pr-8 bg-slate-600 border border-slate-500  text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                                {currentSearch && (
                                    <button
                                        onClick={clearSearch}
                                        className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Filter by Category */}
                        <div className="md:w-48">
                            <label htmlFor="filter" className="block text-xs font-medium text-slate-300 mb-1">
                                Filter by Category
                            </label>
                            <select
                                id="filter"
                                value={currentFilter}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-1.5 bg-slate-600 border border-slate-500  text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            >
                                <option value="">All Categories</option>
                                <option value="workout,meal">Workouts & Meals</option>
                                <option value="workout">Workouts Only</option>
                                <option value="meal">Meals Only</option>
                                <option value="profile">Profiles Only</option>
                                <option value="workout_template,meal_template">Templates</option>
                                <option value="workout_template">Workout Templates</option>
                                <option value="meal_template">Meal Templates</option>
                            </select>
                        </div>

                        {/* Sort */}
                        <div className="md:w-40">
                            <label htmlFor="sort" className="block text-xs font-medium text-slate-300 mb-1">
                                Sort by
                            </label>
                            <select
                                id="sort"
                                value={`${currentSort.sortBy}-${currentSort.sortOrder}`}
                                onChange={handleSortChange}
                                className="w-full px-3 py-1.5 bg-slate-600 border border-slate-500  text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            >
                                <option value="timestamp-desc">Newest First</option>
                                <option value="timestamp-asc">Oldest First</option>
                                <option value="name-asc">Name A-Z</option>
                                <option value="name-desc">Name Z-A</option>
                                <option value="category-asc">Category A-Z</option>
                                <option value="category-desc">Category Z-A</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TableControls;